import Cookies from "js-cookie";

import { EventPipeline } from "@dndbeyond/event-pipeline-lib/client";
import { Region, Source } from "@dndbeyond/event-pipeline-lib/shared";
import type { BaseEvent } from "@dndbeyond/event-pipeline-lib/shared";

import config from "~/config";
import type { AppUser } from "~/hooks/useUser";

import { getIpAddress } from "../helpers/ipAddress";
import { getSubscriptionLevel } from "../helpers/subscriptionLevel";
import { buildCharacterActionEvent } from "./characterAction";
import { buildClickElementEvent } from "./clickElement";
import type {
  CharacterActionInput,
  ClickElementInput,
  CommonEventFields,
  EventContext,
} from "./types";

// Session and visitor cookies are created and managed by event-pipeline-lib
const SESSION_ID_COOKIE = "ddb_sid";
const VISITOR_ID_COOKIE = "ddb_vid";

// A single EventPipeline is shared by the whole app so only one Ketch
// consent listener is registered.
let eventPipeline: EventPipeline | null = null;
// In development, default to true for easier testing
let hasAnalyticsConsent = process.env.NODE_ENV === "development";
let currentUser: AppUser | null | undefined;
// Set when prepInstrumentation runs before events can be sent, so the IP
// lookup starts once they can
let hasPendingPrep = false;

export const getRegion = () =>
  config.environment === "production" ? Region.UsEast1 : Region.UsEast2;

// Starts the IP lookup prepInstrumentation asked for, once events can be sent
const runPendingPrep = () => {
  if (!hasPendingPrep || !hasAnalyticsConsent || !eventPipeline) return;
  hasPendingPrep = false;
  getIpAddress();
};

/**
 * Creates the EventPipeline and starts listening for analytics consent.
 * Safe to call more than once.
 */
export const initInstrumentation = () => {
  if (eventPipeline) return;

  eventPipeline = new EventPipeline(getRegion(), (consentData) => {
    hasAnalyticsConsent =
      consentData === null
        ? // Ketch not available - allow events in development, block otherwise
          process.env.NODE_ENV === "development"
        : consentData.analytics;
    runPendingPrep();
  });
  // Consent can already be allowed in development
  runPendingPrep();
};

/**
 * Updates the user that events are sent with
 */
export const setInstrumentationUser = (user: AppUser | null | undefined) => {
  currentUser = user;
};

const getUserId = (user: AppUser | null | undefined) => {
  const userId = Number(user?.id);
  return user && !Number.isNaN(userId) ? userId : undefined;
};

/**
 * Builds and sends one event when analytics consent allows it. Never throws.
 */
const sendEvent = async (
  buildEvent: (common: CommonEventFields) => BaseEvent
) => {
  const pipeline = eventPipeline;
  if (!hasAnalyticsConsent || !pipeline) return null;

  try {
    // Read everything about the moment of the interaction before waiting for
    // the IP lookup, which can take long enough for the user to navigate away
    const snapshot = {
      pageUrl: globalThis.location?.href || "",
      pagePath: globalThis.location?.pathname || "",
      region: getRegion(),
      subscriptionLevel: getSubscriptionLevel(currentUser),
      userId: getUserId(currentUser),
      sessionId: Cookies.get(SESSION_ID_COOKIE),
      visitorId: Cookies.get(VISITOR_ID_COOKIE),
    };

    const event = buildEvent({ ...snapshot, ipAddress: await getIpAddress() });

    return await pipeline.sendEvent(event, Source.Web);
  } catch {
    return null;
  }
};

/**
 * Starts the IP lookup ahead of time on pages whose clicks navigate away, so
 * their events don't have to wait for it. Pages call this on mount, which
 * can be before the pipeline is created or consent is known, so in that case
 * the lookup waits until events can be sent. Never runs without analytics
 * consent, like the events themselves.
 */
export const prepInstrumentation = () => {
  hasPendingPrep = true;
  runPendingPrep();
};

/**
 * Fires the CHARACTER_ACTION event. Never throws.
 * https://wizardsofthecoast.atlassian.net/wiki/x/HAAWN
 */
export const sendCharacterAction = (
  input: CharacterActionInput,
  context: EventContext & { characterId: number | null }
) =>
  sendEvent((common) =>
    buildCharacterActionEvent({ ...input, ...common, ...context })
  );

/**
 * Fires the CLICK_ELEMENT event. Never throws.
 * https://wizardsofthecoast.atlassian.net/wiki/spaces/D3/pages/815498811/Click+Element+v2
 */
export const sendClickElement = (
  input: ClickElementInput,
  context: EventContext = {}
) =>
  sendEvent((common) =>
    buildClickElementEvent({ ...input, ...common, ...context })
  );

export const resetInstrumentationForTests = () => {
  eventPipeline = null;
  hasAnalyticsConsent = process.env.NODE_ENV === "development";
  currentUser = undefined;
  hasPendingPrep = false;
};
